import { createRouter, createWebHistory } from 'vue-router';

import { frontendURL } from '../helper/URLHelper';
import dashboard from './dashboard/dashboard.routes';
import store from 'dashboard/store';
import { validateLoggedInRoutes } from '../helper/routeHelpers';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';

const ONBOARDING_STEPS = ['account_details'];
const routes = [...dashboard.routes];

export const router = createRouter({ history: createWebHistory(), routes });

const getRouteFeatureFlags = to => {
  const metas = [to.meta, ...(to.matched || []).map(record => record.meta)];
  return [...new Set(metas.map(meta => meta?.featureFlag).filter(Boolean))];
};

const isRouteFeatureEnabled = async (to, accountId) => {
  const flags = getRouteFeatureFlags(to);
  if (!flags.length) return true;

  // The account may not be in the store yet on the first navigation.
  if (!store.getters['accounts/getAccount'](accountId).id) {
    await store.dispatch('accounts/get', { silent: true, accountId });
  }
  const isEnabled = store.getters['accounts/isFeatureEnabledonAccount'];
  return flags.every(flag => isEnabled(accountId, flag));
};

export const validateAuthenticateRoutePermission = async (to, next) => {
  const { isLoggedIn, getCurrentUser: user } = store.getters;

  if (!isLoggedIn) {
    window.location.assign('/app/login');
    return '';
  }

  const { accounts = [], account_id: accountId } = user;

  if (!accounts.length) {
    if (to.name === 'no_accounts') {
      return next();
    }
    return next(frontendURL('no-accounts'));
  }

  const routeAccountId = Number(to.params?.accountId || accountId);
  const userAccount = accounts.find(a => a.id === routeAccountId);
  const isAdmin = userAccount?.role === 'administrator';
  const isActive = userAccount?.status === 'active';
  const needsOnboarding =
    ONBOARDING_STEPS.includes(userAccount?.onboarding_step) &&
    isAdmin &&
    isActive;

  if (to.name === 'no_accounts' || !to.name) {
    const target = needsOnboarding ? 'onboarding' : 'dashboard';
    return next(frontendURL(`accounts/${routeAccountId}/${target}`));
  }

  if (needsOnboarding && !isOnOnboardingView(to)) {
    return next(frontendURL(`accounts/${routeAccountId}/onboarding`));
  }
  if (!needsOnboarding && isOnOnboardingView(to)) {
    return next(frontendURL(`accounts/${routeAccountId}/dashboard`));
  }

  const nextRoute = validateLoggedInRoutes(to, store.getters.getCurrentUser);
  if (nextRoute) return next(frontendURL(nextRoute));

  if (!(await isRouteFeatureEnabled(to, routeAccountId))) {
    return next(frontendURL(`accounts/${routeAccountId}/dashboard`));
  }
  return next();
};

export const initalizeRouter = () => {
  const userAuthentication = store.dispatch('setUser');

  router.beforeEach(async (to, _from, next) => {
    await userAuthentication;
    await validateAuthenticateRoutePermission(to, next, store);
  });
};

export default router;
